import { HttpClient, type HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ApiHttpClient } from '@core/http/api-http-client';
import type { AxelorWsAppInfoResponse } from '@core/http/axelor/contracts/axelor-app-info.types';
import type { AxelorLoginRequest } from '@core/http/axelor/contracts/axelor-auth.types';
import {
  buildAxelorCallbackPath,
  buildAxelorLogoutPath,
  buildAxelorWsAppInfoPath,
} from '@core/http/axelor/utils/axelor-paths';
import { type Observable } from 'rxjs';

/** Публичный контракт для {@link AuthService} (login → GET корня → ws/app/info → logout). */
export interface AxelorAuthClientApi {
  getAppRootAfterLogin(): Observable<HttpResponse<string>>;
  getWsAppInfo(): Observable<AxelorWsAppInfoResponse>;
  login(request: AxelorLoginRequest): Observable<HttpResponse<string>>;
  logout(): Observable<HttpResponse<string>>;
}

/**
 * Axelor session login/logout at app root paths (`/callback`, `/logout`), same origin as `apiBaseUrl`.
 * Uses `responseType: 'text'` and `observe: 'response'` because Axelor may return JSON string or HTML.
 * Logout uses POST with empty body (как в типичной интеграции Axelor SPA).
 */
@Injectable({ providedIn: 'root' })
export class AxelorAuthClient implements AxelorAuthClientApi {
  private readonly api = inject(ApiHttpClient);
  private readonly http = inject(HttpClient);

  /**
   * GET на корень приложения (`apiBaseUrl` + `/`), как `API_URLS.BASE_URL_MAIN` в smart-customs.
   * После успешного `POST /callback` legacy вызывает этот шаг перед проверкой face-id и навигацией.
   */
  public getAppRootAfterLogin(): Observable<HttpResponse<string>> {
    const url = this.buildAppRootUrl();
    return this.http.get(url, {
      context: this.api.createContext({}),
      observe: 'response',
      responseType: 'text',
    });
  }

  private buildAppRootUrl(): string {
    const base = this.api.buildUrl('').replace(/\/+$/, '');
    return `${base}/`;
  }

  /**
   * Мета-приложения и текущий пользователь (после установки cookie сессии).
   */
  public getWsAppInfo(): Observable<AxelorWsAppInfoResponse> {
    const url = this.api.buildUrl(buildAxelorWsAppInfoPath());
    return this.http.get<AxelorWsAppInfoResponse>(url, {
      context: this.api.createContext({}),
    });
  }

  public login(request: AxelorLoginRequest): Observable<HttpResponse<string>> {
    const url = this.api.buildUrl(buildAxelorCallbackPath());
    return this.http.post(url, request, {
      context: this.api.createContext({}),
      observe: 'response',
      responseType: 'text',
    });
  }

  public logout(): Observable<HttpResponse<string>> {
    const url = this.api.buildUrl(buildAxelorLogoutPath());
    return this.http.post(
      url,
      {},
      {
        context: this.api.createContext({}),
        observe: 'response',
        responseType: 'text',
      },
    );
  }
}
